/**
 * Modal dialog: rendered into <body>, focus moves inside and is trapped (Tab / Shift+Tab cycle), Esc or the backdrop
 * closes it, focus returns to where it was. The page behind should be made `inert` by the caller while it is open.
 */
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

interface Props {
  titleId: string;
  onClose: () => void;
  initialFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** added to the box's "dialog" class (the camera dialog is wider, full-screen on a phone) */
  className?: string;
}

export function Modal({ titleId, onClose, initialFocus, children, className }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const first = initialFocus?.current ?? box.current?.querySelector<HTMLElement>(FOCUSABLE) ?? box.current;
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close.current();
      }
    };
    document.addEventListener("keydown", onKey); // also when focus sits in a third-party iframe's parent
    return () => {
      document.removeEventListener("keydown", onKey);
      before?.focus?.();
    };
  }, [initialFocus]);

  function trap(e: ReactKeyboardEvent) {
    if (e.key !== "Tab" || !box.current) return;
    const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.closest("[hidden]"));
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === box.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} className={className ? `dialog ${className}` : "dialog"} role="dialog" aria-modal="true"
           aria-labelledby={titleId} tabIndex={-1} onKeyDown={trap}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
